import { useState, useRef } from 'react'
import { Upload, X, Image as ImageIcon, Loader } from 'lucide-react'
import { supabase } from '../../lib/supabase'

export default function ImageUpload({ images = [], onChange, maxImages = 5 }) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState('')
  const fileInputRef = useRef(null)

  const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files)
    if (!files.length) return

    // Check if adding these files would exceed the limit
    if (images.length + files.length > maxImages) {
      setError(`Maximum ${maxImages} images allowed`)
      return
    }

    setIsUploading(true)
    setError('')
    setUploadProgress(0)

    const newImageUrls = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed')
        continue
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Images must be under 5MB')
        continue
      }

      try {
        // Generate unique filename
        const fileExt = file.name.split('.').pop()
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`
        const filePath = `products/${fileName}`

        // Upload to Supabase Storage
        const { data, error: uploadError } = await supabase.storage
          .from('product-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false,
          })

        if (uploadError) {
          console.error('Upload error:', uploadError)
          setError(uploadError.message)
          continue
        }

        // Get public URL
        const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/product-images/${filePath}`
        newImageUrls.push(publicUrl)

        // Update progress
        setUploadProgress(((i + 1) / files.length) * 100)
      } catch (err) {
        console.error('Upload failed:', err)
        setError('Failed to upload image')
      }
    }

    // Update parent with new images
    if (newImageUrls.length > 0) {
      onChange([...images.filter(img => img), ...newImageUrls])
    }

    setIsUploading(false)
    setUploadProgress(0)

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemove = async (index) => {
    const imageUrl = images[index]

    // Try to delete from Supabase Storage if it's a Supabase URL
    if (imageUrl && imageUrl.includes('supabase.co/storage')) {
      try {
        const filePath = imageUrl.split('/product-images/')[1]
        if (filePath) {
          await supabase.storage.from('product-images').remove([filePath])
        }
      } catch (err) {
        console.error('Error deleting image:', err)
      }
    }

    // Update parent
    const newImages = images.filter((_, i) => i !== index)
    onChange(newImages.length ? newImages : [''])
  }

  const handleUrlChange = (index, url) => {
    const newImages = [...images]
    newImages[index] = url
    onChange(newImages)
  }

  return (
    <div className="space-y-4">
      {/* Image Preview Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {images.map((image, index) => (
          <div
            key={index}
            className="relative aspect-square bg-pp-sand/30 rounded-xl overflow-hidden border-2 border-dashed border-pp-sand"
          >
            {image ? (
              <>
                <img
                  src={image}
                  alt={`Product ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
                <div className="hidden absolute inset-0 items-center justify-center bg-pp-sand/50">
                  <ImageIcon className="w-8 h-8 text-pp-stone" />
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full text-red-500 hover:bg-white transition-colors shadow-sm"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <ImageIcon className="w-8 h-8 text-pp-stone" />
              </div>
            )}
          </div>
        ))}

        {/* Add Image Button */}
        {images.length < maxImages && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="aspect-square bg-pp-sand/30 rounded-xl border-2 border-dashed border-pp-sand hover:border-pp-terracotta hover:bg-pp-sand/50 transition-colors flex flex-col items-center justify-center gap-2"
          >
            {isUploading ? (
              <>
                <Loader className="w-6 h-6 text-pp-terracotta animate-spin" />
                <span className="text-xs text-pp-stone">{Math.round(uploadProgress)}%</span>
              </>
            ) : (
              <>
                <Upload className="w-6 h-6 text-pp-stone" />
                <span className="text-xs text-pp-stone">Upload</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}

      {/* Manual URL Input */}
      <div className="space-y-2">
        <p className="text-xs text-pp-stone">Or enter image URLs manually:</p>
        {images.map((image, index) => (
          <div key={index} className="flex gap-2">
            <input
              type="url"
              value={image || ''}
              onChange={(e) => handleUrlChange(index, e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="flex-1 px-3 py-2 border border-pp-sand rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pp-terracotta/20"
            />
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="p-2 text-pp-stone hover:text-red-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        {images.length < maxImages && (
          <button
            type="button"
            onClick={() => onChange([...images, ''])}
            className="text-sm text-pp-terracotta hover:underline"
          >
            + Add image URL
          </button>
        )}
      </div>

      <p className="text-xs text-pp-stone">
        Accepted formats: JPG, PNG, WebP. Max 5MB per image. {maxImages} images max.
      </p>
    </div>
  )
}
